/**
 * Minimal WebAudio engine: lazy AudioContext (resumed on the first user
 * gesture — the Open click), a decoded-buffer cache, gain-node volume, and
 * silent degradation on any failure. No HTMLAudio.
 */
export class SoundPlayer {
  private context: AudioContext | null = null;
  private gainNode: GainNode | null = null;
  private buffers = new Map<string, Promise<AudioBuffer | null>>();
  private volumeValue = 1;

  get volume(): number {
    return this.volumeValue;
  }

  setVolume(volume: number): void {
    this.volumeValue = Math.max(0, Math.min(1, volume));
    if (this.gainNode) this.gainNode.gain.value = this.volumeValue;
  }

  private ensureContext(): AudioContext | null {
    if (this.context) return this.context;
    if (typeof window === "undefined") return null;
    const Ctor =
      window.AudioContext ??
      (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    try {
      this.context = new Ctor();
      this.gainNode = this.context.createGain();
      this.gainNode.gain.value = this.volumeValue;
      this.gainNode.connect(this.context.destination);
    } catch {
      this.context = null;
      this.gainNode = null;
    }
    return this.context;
  }

  /** Call from a user gesture (the open click) so playback is allowed. */
  unlock(): void {
    const context = this.ensureContext();
    if (context && context.state === "suspended") {
      context.resume().catch(() => {});
    }
  }

  /** Fetch + decode a sound into the cache (deduped per URL). */
  preload(url: string): void {
    void this.getBuffer(url);
  }

  private getBuffer(url: string): Promise<AudioBuffer | null> {
    const cached = this.buffers.get(url);
    if (cached) return cached;
    const context = this.ensureContext();
    const pending = context
      ? fetch(url)
          .then((response) => (response.ok ? response.arrayBuffer() : null))
          .then((data) => (data ? context.decodeAudioData(data) : null))
          .catch(() => null)
      : Promise.resolve(null);
    this.buffers.set(url, pending);
    return pending;
  }

  /** Play a sound; every failure path is silent by design. */
  play(url: string | undefined, options?: { volume?: number }): void {
    if (!url) return;
    const context = this.ensureContext();
    if (!context || !this.gainNode) return;
    void this.getBuffer(url).then((buffer) => {
      if (!buffer || !this.gainNode) return;
      try {
        const source = context.createBufferSource();
        source.buffer = buffer;
        if (options?.volume !== undefined) {
          const gain = context.createGain();
          gain.gain.value = Math.max(0, Math.min(1, options.volume));
          source.connect(gain);
          gain.connect(this.gainNode);
        } else {
          source.connect(this.gainNode);
        }
        source.start();
      } catch {
        // Degrade silently.
      }
    });
  }

  dispose(): void {
    this.buffers.clear();
    this.context?.close().catch(() => {});
    this.context = null;
    this.gainNode = null;
  }
}
